import { hasPermission, PERMISSIONS } from '@/lib/permissions'

/** @typedef {'main' | 'admin' | 'masters' | 'workspace'} NavGroupId */

/**
 * @typedef {Object} AppNavEntry
 * @property {string} id
 * @property {string} path
 * @property {NavGroupId} group
 * @property {string} [labelKey]
 * @property {string} [label]
 * @property {string} [permission]
 * @property {string[]} [keywords]
 * @property {string} icon
 */

/** @type {AppNavEntry[]} */
export const APP_NAV_ENTRIES = [
  {
    id: 'command',
    labelKey: 'nav.command',
    path: '/',
    group: 'main',
    icon: 'layout-dashboard',
    keywords: ['dashboard', 'home', 'command', 'overview'],
  },
  {
    id: 'calendar',
    labelKey: 'nav.calendar',
    path: '/calendar',
    group: 'main',
    permission: PERMISSIONS.APPOINTMENTS_MANAGE,
    icon: 'calendar',
    keywords: ['schedule', 'appointments', 'bookings'],
  },
  {
    id: 'online-bookings',
    labelKey: 'nav.onlineBookings',
    path: '/online-bookings',
    group: 'main',
    permission: PERMISSIONS.APPOINTMENTS_MANAGE,
    icon: 'globe',
    keywords: ['online', 'guest', 'web', 'approval', 'pending'],
  },
  {
    id: 'pos',
    labelKey: 'nav.pos',
    path: '/pos',
    group: 'main',
    permission: PERMISSIONS.POS_ACCESS,
    icon: 'receipt',
    keywords: ['point of sale', 'checkout', 'ticket', 'retail'],
  },
  {
    id: 'customers',
    labelKey: 'nav.guests',
    path: '/customers',
    group: 'main',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'contact',
    keywords: ['guests', 'clients', 'crm'],
  },
  {
    id: 'stylists',
    labelKey: 'nav.team',
    path: '/stylists',
    group: 'main',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'users',
    keywords: ['team', 'staff', 'stylists'],
  },
  {
    id: 'hq',
    label: 'HQ dashboard',
    path: '/hq',
    group: 'main',
    permission: PERMISSIONS.HQ_VIEW,
    icon: 'building',
    keywords: ['hq', 'multi-branch', 'enterprise', 'owner'],
  },
  {
    id: 'insights',
    labelKey: 'nav.insights',
    path: '/reports',
    group: 'main',
    permission: PERMISSIONS.REPORTS_VIEW,
    icon: 'bar-chart',
    keywords: ['reports', 'analytics', 'insights'],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    path: '/inventory',
    group: 'main',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'boxes',
    keywords: ['stock', 'products', 'warehouse'],
  },
  {
    id: 'accounts',
    label: 'Accounts',
    path: '/accounts',
    group: 'main',
    permission: PERMISSIONS.ACCOUNTS_ACCESS,
    icon: 'landmark',
    keywords: ['finance', 'ledger', 'payments'],
  },
  {
    id: 'admin-company',
    label: 'Company setup',
    path: '/admin/company',
    group: 'admin',
    permission: PERMISSIONS.ADMIN_FULL,
    icon: 'building',
    keywords: ['company', 'organization', 'settings'],
  },
  {
    id: 'admin-branches',
    label: 'Branch setup',
    path: '/admin/branches',
    group: 'admin',
    permission: PERMISSIONS.ADMIN_FULL,
    icon: 'map-pin',
    keywords: ['branches', 'locations', 'salon'],
  },
  {
    id: 'admin-users',
    label: 'Users & roles',
    path: '/admin/users',
    group: 'admin',
    permission: PERMISSIONS.ADMIN_USERS,
    icon: 'users',
    keywords: ['users', 'roles', 'permissions', 'staff login'],
  },
  {
    id: 'admin-audit',
    label: 'Audit log',
    path: '/admin/audit-logs',
    group: 'admin',
    permission: PERMISSIONS.ADMIN_FULL,
    icon: 'scroll-text',
    keywords: ['audit', 'history', 'activity'],
  },
  {
    id: 'service-categories',
    label: 'Service categories',
    path: '/masters/service-categories',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'layers',
    keywords: ['categories', 'menu', 'catalog'],
  },
  {
    id: 'services',
    label: 'Services',
    path: '/masters/services',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'scissors',
    keywords: ['treatments', 'menu items', 'pricing'],
  },
  {
    id: 'chairs',
    label: 'Chairs',
    path: '/masters/chairs',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'armchair',
    keywords: ['stations', 'resources'],
  },
  {
    id: 'packages',
    label: 'Packages',
    path: '/masters/packages',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'gift',
    keywords: ['bundles', 'deals'],
  },
  {
    id: 'products',
    label: 'Products',
    path: '/masters/products',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'package',
    keywords: ['retail', 'sku'],
  },
  {
    id: 'catalog-settings',
    label: 'Catalog settings',
    path: '/masters/catalog',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'settings',
    keywords: ['online catalog', 'booking settings'],
  },
  {
    id: 'pos-quick-keys',
    label: 'POS quick keys',
    path: '/masters/pos-quick-keys',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'zap',
    keywords: ['shortcuts', 'hotkeys'],
  },
  {
    id: 'import',
    label: 'CSV import',
    path: '/masters/import',
    group: 'masters',
    permission: PERMISSIONS.MASTERS_MANAGE,
    icon: 'upload',
    keywords: ['import', 'bulk', 'spreadsheet'],
  },
  {
    id: 'stylist-workspace',
    label: 'My day',
    path: '/stylist',
    group: 'workspace',
    permission: PERMISSIONS.STYLIST_WORKSPACE,
    icon: 'scissors',
    keywords: ['stylist', 'schedule', 'tablet', 'workspace'],
  },
  {
    id: 'commissions',
    label: 'My commissions',
    path: '/workspace/commissions',
    group: 'workspace',
    permission: PERMISSIONS.STYLIST_WORKSPACE,
    icon: 'wallet',
    keywords: ['earnings', 'stylist', 'pay'],
  },
]

/** @type {Record<NavGroupId, string>} */
export const NAV_GROUP_LABEL_KEYS = {
  main: 'search.groups.main',
  admin: 'search.groups.admin',
  masters: 'search.groups.masters',
  workspace: 'search.groups.workspace',
}

/** @type {NavGroupId[]} */
export const NAV_GROUP_ORDER = ['main', 'admin', 'masters', 'workspace']

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @returns {AppNavEntry[]}
 */
export function getVisibleAppNavigation(user) {
  return APP_NAV_ENTRIES.filter((entry) => {
    if (entry.permission && !hasPermission(user, entry.permission)) {
      return false
    }
    return true
  })
}

/**
 * @param {AppNavEntry} entry
 * @param {string} pathname
 */
export function isAppNavActive(entry, pathname) {
  if (entry.path === '/') {
    return pathname === '/'
  }
  return pathname === entry.path || pathname.startsWith(`${entry.path}/`)
}

/**
 * @param {import('@/types/api').AuthUser | null | undefined} user
 * @param {string} query
 * @param {(key: string) => string} t
 * @returns {AppNavEntry[]}
 */
export function filterAppNavigation(user, query, t) {
  const normalizedQuery = query.trim().toLowerCase()

  return APP_NAV_ENTRIES.filter((entry) => {
    if (entry.permission && !hasPermission(user, entry.permission)) {
      return false
    }

    if (!normalizedQuery) {
      return true
    }

    const label = entry.labelKey ? t(entry.labelKey) : (entry.label ?? '')
    const haystack = [label, ...(entry.keywords ?? []), entry.path].join(' ').toLowerCase()

    return haystack.includes(normalizedQuery)
  })
}

/**
 * @param {AppNavEntry[]} entries
 * @returns {Record<NavGroupId, AppNavEntry[]>}
 */
export function groupAppNavigation(entries) {
  /** @type {Record<NavGroupId, AppNavEntry[]>} */
  const grouped = {
    main: [],
    admin: [],
    masters: [],
    workspace: [],
  }

  entries.forEach((entry) => {
    grouped[entry.group].push(entry)
  })

  return grouped
}

/**
 * @param {AppNavEntry} entry
 * @param {(key: string) => string} t
 */
export function appNavLabel(entry, t) {
  return entry.labelKey ? t(entry.labelKey) : (entry.label ?? entry.path)
}
